import { useRef, useCallback, useEffect } from 'react';
import { fields } from '../common/useStreamProcessorNew';
import { parseJsonArrayFields } from '../common/jsonArrayFields';
import { useCompLookups } from '../common/useCompLookups';
import { usePersistedDataStore } from '../components/store';
import logger from '../utils/logger';
import { getExistingCompIds } from '../components/CaseReview/common/compSheetLookup';

/** Poll saved-comp enqueue while /enrich_client_cases is in flight (parallel KNN). */
const ENRICH_RECHECK_DELAY_MS = 150;
const ENRICH_RECHECK_MAX_ATTEMPTS = 200;

export const useStreamProcessorWorker = () => {
  const workerRef           = useRef(null);
  const callbacksRef        = useRef(new Map());
  const isProcessingRef     = useRef(false);
  const processedChunksRef  = useRef([]);
  // Store compStreamObjectRef across calls so handlers can access it without closure capture
  const compStreamObjRef    = useRef(null);
  // Store negotiationObj for use in comp lookups
  const negotiationObjRef   = useRef(null);
  const handleChunkProcessedRef = useRef(null);
  const handleServerErrorRef    = useRef(null);
  const handleStreamCompleteRef = useRef(null);

  const compLookups = useCompLookups({ maxConcurrent: 8, batchThreshold: 15 });

  const tryEnqueueMissingSavedCompsForCase = useCallback((ref, caseIndex, attempt = 0) => {
    if (!ref?.properties || caseIndex < 0) return;
    const regularComps = ref.properties[0]?.[caseIndex];
    if (!regularComps) return;

    const store = usePersistedDataStore.getState();
    const neg = store.negotiationObj;
    const enriching = store.isEnrichingClientCases;
    const taxYear = neg?.TaxYear;
    if (!taxYear) return;

    const savedCompIds = Object.values(neg?.cases?.[caseIndex]?.Comps ?? {});
    const reportType = neg?.reportType || 'standard';
    const existingCompIds = getExistingCompIds(regularComps, reportType);

    if (savedCompIds.length) {
      compLookups.enqueueMissingSavedComps({
        savedCompIds,
        existingCompIds,
        taxYear,
        ref,
        caseIndex,
        negotiationObj: neg,
        reportType,
      });
      return;
    }

    if (enriching && attempt < ENRICH_RECHECK_MAX_ATTEMPTS) {
      setTimeout(
        () => tryEnqueueMissingSavedCompsForCase(ref, caseIndex, attempt + 1),
        ENRICH_RECHECK_DELAY_MS,
      );
    }
  }, [compLookups]);

  // ── server error ───────────────────────────────────────────────────────────
  const handleServerError = useCallback((data) => {
    const { chunkIndex, totalChunks, error } = data;
    logger.error(`🚨 Server error at chunk ${chunkIndex + 1}/${totalChunks}:`, error);

    const progress = totalChunks > 0 ? ((chunkIndex + 1) / totalChunks) * 100 : 0;
    callbacksRef.current.get('statefulUpdate')?.({ type: 'server_error', error, chunkIndex, progress });
    callbacksRef.current.get('completion')?.({ type: 'server_error', error, chunkIndex, shouldRetry: true });
  }, []);

  // ── chunk processed ────────────────────────────────────────────────────────
  const handleChunkProcessed = useCallback((data) => {
    const { chunkIndex, totalChunks, result, progress } = data;

    if (!result.success) {
      logger.error('Chunk processing failed:', result.error);
      callbacksRef.current.get('statefulUpdate')?.({ type: 'chunk_error', error: result.error, chunkIndex, progress });
      return;
    }

    let processedData = result.data;
    const compRef = compStreamObjRef.current;

    if (result.data.isMarketing && result.data.marketingResponse) {
      // Marketing: just re-process the response, no ref writes needed
      processedData = {
        ...result.data,
        marketingResponse: processMarketingResponse(result.data.marketingResponse, result.data.currentIndex),
      };
    } else {
      // ── 1. Write raw comp data into compStreamObjectRef (same as direct path) ──
      const { index, regularComps, optimizedComps } = result.data;
      if (compRef?.properties && index >= 0) {
        compRef.properties[0][index] = regularComps;
        compRef.properties[1][index] = optimizedComps;
        compRef.updated[index] = true;
      }

      // ── 2. Enqueue missing saved comps — fully fire-and-forget ─────────────
      // Uses fresh store negotiationObj; if enrich_client_cases is still loading, polls until Comps exist or enriching ends.
      if (compRef && index >= 0) {
        tryEnqueueMissingSavedCompsForCase(compRef, index);
      }
    }

    processedChunksRef.current.push(processedData);

    // ── 3. Notify UI — unchanged from original ────────────────────────────
    callbacksRef.current.get('polling')?.( processedData);
    callbacksRef.current.get('statefulUpdate')?.({ type: 'chunk_processed', data: processedData, progress });
  }, [compLookups, tryEnqueueMissingSavedCompsForCase]);

  // ── stream complete ────────────────────────────────────────────────────────
  const handleStreamComplete = useCallback((data) => {
    logger.log('🎉 Stream complete. Processed:', data.totalProcessed);
    isProcessingRef.current = false;

    callbacksRef.current.get('completion')?.({
      type: 'stream_complete',
      data: { ...data, processedChunks: processedChunksRef.current },
    });

    processedChunksRef.current = [];
    // NOTE: do NOT clear the comp lookup queue here — background lookups
    // may still be resolving and writing into the ref legitimately.
  }, []);

  // ── marketing helper ───────────────────────────────────────────────────────
  const processMarketingResponse = (marketingResponse, currentIndex) =>
    fields.reduce(
      (row, { field }) => ({
        ...row,
        id: currentIndex,
        [field]: marketingResponse?.[field] ?? '',
      }),
      {}
    );

  // ── main entry point ───────────────────────────────────────────────────────
  const processStreamWithWorker = useCallback(async ({
    stream,
    negotiationObj,
    compStreamObjectRef,
    onStatefulUpdate,
    onCompletion,
    onPollingUpdate,
  }) => {
    if (isProcessingRef.current) {
      logger.warn('Stream processing already in progress');
      return;
    }

    if (!compStreamObjectRef) {
      throw new Error('comp stream object is missing');
    }

    // Store refs so message handlers (which have no closure over these args) can reach them
    compStreamObjRef.current   = compStreamObjectRef;
    negotiationObjRef.current  = negotiationObj;

    if (onStatefulUpdate) callbacksRef.current.set('statefulUpdate', onStatefulUpdate);
    if (onCompletion)     callbacksRef.current.set('completion',     onCompletion);
    if (onPollingUpdate)  callbacksRef.current.set('polling',        onPollingUpdate);

    processedChunksRef.current = [];

    // Initialise compStreamObjectRef slots if needed
    if (!compStreamObjectRef.properties) {
      const lookupArray = negotiationObj.inventoryUpdateRun
        ? negotiationObj.originalPidOrder
        : negotiationObj.uids;
      compStreamObjectRef.properties = [
        new Array(lookupArray.length).fill(null),
        new Array(lookupArray.length).fill(null),
      ];
      compStreamObjectRef.updated = new Array(lookupArray.length).fill(false);
    }

    isProcessingRef.current = true;

    if (!workerRef.current) {
      await processStreamDirectly(stream, negotiationObj, compStreamObjectRef, onStatefulUpdate, onCompletion, onPollingUpdate);
      return;
    }

    let reader = null;
    try {
      reader = stream.getReader();
      const decoder = new TextDecoder('utf-8');
      let buffer = '';
      let chunkIndex = 0;
      let totalChunks = 0;

      while (true) {
        const { done, value } = await reader.read();

        if (done) {
          if (buffer.trim()) {
            workerRef.current.postMessage({
              type: 'PROCESS_STREAM_CHUNK',
              data: { chunk: buffer, negotiationObj, chunkIndex, totalChunks: totalChunks || 1, compStreamObjectRef },
            });
            chunkIndex++;
            totalChunks = Math.max(totalChunks, chunkIndex);
          }
          break;
        }

        buffer += decoder.decode(value, { stream: true });

        while (buffer.includes('\n')) {
          const nl = buffer.indexOf('\n');
          const chunk = buffer.slice(0, nl);
          buffer = buffer.slice(nl + 1);

          if (chunk.trim()) {
            workerRef.current.postMessage({
              type: 'PROCESS_STREAM_CHUNK',
              data: { chunk, negotiationObj, chunkIndex, totalChunks: totalChunks || 1, compStreamObjectRef },
            });
            chunkIndex++;
            totalChunks = Math.max(totalChunks, chunkIndex + 1);
          }
        }
      }

      workerRef.current.postMessage({
        type: 'PROCESS_STREAM_COMPLETE',
        data: { negotiationObj, compStreamObjectRef, processedChunks: processedChunksRef.current },
      });

      onCompletion?.();

    } catch (err) {
      logger.error('Error in stream processing with worker:', err);
      throw err;
    } finally {
      isProcessingRef.current = false;
      reader?.releaseLock();
    }
  }, []);

  // ── direct (no-worker) fallback ────────────────────────────────────────────
  const processStreamDirectly = useCallback(async (
    stream, negotiationObj, compStreamObjectRef,
    onStatefulUpdate, onCompletion, onPollingUpdate,
  ) => {
    try {
      const reader  = stream.getReader();
      const decoder = new TextDecoder('utf-8');
      let buffer = '';
      let chunkIndex = 0;

      if (!compStreamObjectRef.properties) {
        const lookupArray = negotiationObj.inventoryUpdateRun
          ? negotiationObj.originalPidOrder
          : negotiationObj.uids;
        compStreamObjectRef.properties = [
          new Array(lookupArray.length).fill(null),
          new Array(lookupArray.length).fill(null),
        ];
        compStreamObjectRef.updated = new Array(lookupArray.length).fill(false);
      }

      while (true) {
        const { done, value } = await reader.read();
        if (done) {
          if (buffer.trim()) {
            try {
              await processUpdateDirectly(JSON.parse(buffer), negotiationObj, compStreamObjectRef, chunkIndex, onStatefulUpdate, onPollingUpdate);
              chunkIndex++;
            } catch (e) { logger.error('Error processing final buffer:', e); }
          }
          break;
        }

        buffer += decoder.decode(value, { stream: true });

        while (buffer.includes('\n')) {
          const nl    = buffer.indexOf('\n');
          const chunk = buffer.slice(0, nl);
          buffer = buffer.slice(nl + 1);

          if (chunk.trim()) {
            try {
              await processUpdateDirectly(JSON.parse(chunk), negotiationObj, compStreamObjectRef, chunkIndex, onStatefulUpdate, onPollingUpdate);
              chunkIndex++;
            } catch (e) { logger.error('Error processing chunk:', e); }
          }
        }
      }

      reader.releaseLock();
      onCompletion?.();
    } catch (err) {
      logger.error('Error in direct stream processing:', err);
      throw err;
    } finally {
      isProcessingRef.current = false;
    }
  }, []);

  // ── direct update handler (fallback path) ─────────────────────────────────
  const processUpdateDirectly = useCallback(async (
    update, negotiationObj, compStreamObjectRef,
    chunkIndex, onStatefulUpdate, onPollingUpdate,
  ) => {
    // Server error check
    if (
      typeof update === 'object' && !Array.isArray(update) && update !== null &&
      ((update.status === 'error' && update.error) ||
       (update.error && typeof update.error === 'string' && update.error.includes('Internal Server Error')))
    ) {
      onStatefulUpdate?.({ type: 'server_error', error: update.error ?? 'Unknown server error', chunkIndex, shouldRetry: true });
      return;
    }

    if (!Array.isArray(update) || update.length < 4 || update[0] === null || update[1] === null) {
      logger.warn('Invalid update format:', update);
      return;
    }

    const [regularComps, optimizedComps, , evalDate] = update;

    if (negotiationObj.reportType === 'marketing') {
      const currentIndex = negotiationObj.currentMarketingIndex || 1;
      negotiationObj.currentMarketingIndex = currentIndex + 1;
      const marketingResponse = processMarketingResponse(regularComps, currentIndex);
      const payload = {
        index: currentIndex - 1, regularComps, optimizedComps, evalDate,
        parcelId: '', isMarketing: true, marketingResponse,
        totalCases: update[3], currentIndex,
      };
      onStatefulUpdate?.({ type: 'chunk_processed', data: payload });
      onPollingUpdate?.(payload);
      return;
    }

    const parcelId = regularComps.parcel_id?.[0] ?? regularComps.ParcelID?.[0] ?? '';
    const lookupArray = negotiationObj.inventoryUpdateRun
      ? negotiationObj.originalPidOrder
      : negotiationObj.uids;
    const index = lookupArray.findIndex(item => item === parcelId);

    if (index < 0) {
      logger.warn('PID not found in lookupArray:', parcelId);
      return;
    }

    // Write into ref
    compStreamObjectRef.properties[0][index] = parseJsonArrayFields(regularComps);
    compStreamObjectRef.properties[1][index] = parseJsonArrayFields(optimizedComps);
    compStreamObjectRef.updated[index] = true;

    tryEnqueueMissingSavedCompsForCase(compStreamObjectRef, index);

    const payload = { index, regularComps, optimizedComps, evalDate, parcelId };
    onPollingUpdate?.(payload);
    onStatefulUpdate?.({ type: 'chunk_processed', data: payload });
  }, [compLookups, tryEnqueueMissingSavedCompsForCase]);

  handleChunkProcessedRef.current = handleChunkProcessed;
  handleServerErrorRef.current = handleServerError;
  handleStreamCompleteRef.current = handleStreamComplete;

  // ── worker init (after handlers; dispatch via refs so callbacks stay current) ─
  useEffect(() => {
    const initializeWorker = async () => {
      if (typeof Worker !== 'undefined') {
        try {
          workerRef.current = new Worker(
            new URL('../workers/streamProcessorWorker.js', import.meta.url)
          );
        } catch (error) {
          logger.warn('Worker failed to initialize, falling back to direct processing:', error);
          return;
        }

        workerRef.current.onmessage = (event) => {
          const { type, data } = event.data;

          switch (type) {
            case 'STREAM_CHUNK_PROCESSED':
              handleChunkProcessedRef.current?.(data);
              break;
            case 'STREAM_SERVER_ERROR':
              handleServerErrorRef.current?.(data);
              break;
            case 'STREAM_PROCESSING_COMPLETE':
              handleStreamCompleteRef.current?.(data);
              break;
            case 'WORKER_STATUS':
              break;
            case 'MISSING_COMPS_STATUS':
              break;
            default:
              logger.warn('Unknown message type from worker:', type);
          }
        };

        workerRef.current.onerror = (error) => {
          logger.warn('Worker error, falling back to direct processing:', error);
        };
      }
    };

    initializeWorker();
    return () => { workerRef.current?.terminate(); workerRef.current = null; };
  }, []);

  // ── public API ─────────────────────────────────────────────────────────────
  const getProcessingStatus  = useCallback(() => ({ isProcessing: isProcessingRef.current, processedChunks: processedChunksRef.current.length, workerReady: !!workerRef.current }), []);
  const getDetailedProgress  = useCallback(() => ({ isProcessing: isProcessingRef.current, processedChunks: processedChunksRef.current, workerReady: !!workerRef.current, lastProcessedChunk: processedChunksRef.current.at(-1) }), []);

  const resetProcessingState = useCallback(() => {
    isProcessingRef.current = false;
    processedChunksRef.current = [];
    callbacksRef.current.clear();
    compLookups.clearQueue(); // ← stop any pending lookups from a previous run
  }, [compLookups]);

  return {
    processStreamWithWorker,
    getProcessingStatus,
    getDetailedProgress,
    resetProcessingState,
    isProcessing: isProcessingRef.current,
    compLookups: {
      getStatus:    compLookups.getStatus,
      hasActiveWork: compLookups.hasActiveWork,
      flush:        compLookups.flush,
      clearQueue:   compLookups.clearQueue,
      getQueueStats: compLookups.getQueueStats,
    },
  };
};